import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertComponent } from '../../shared/alert/alert.component';
import { MobileUsePipe } from '../../../pipes/mobile-use.pipe';
import { EmailUsePipe } from '../../../pipes/email-use.pipe';
import { Email } from '../../../model/email';
import { Phone } from '../../../model/phone';
import { ContactPermission } from '../../../model/contact-permission.enum';
import { MemberUpdate } from '../../../model/member-update';
import { PersonalInformationUpdate } from '../../../model/personal-information-update';
import { Member } from '../../../model/member';
import { UserContextService } from '../../../services/user-context.service';
import { mapContactUpdateErrorByCode } from '../../../utils/backend-error.utils';

@Component({
    selector: 'myabvv-personal-information-contact',
    templateUrl: './personal-information-contact.component.html',
    styleUrl: './personal-information-contact.component.css',
    standalone: true,
    imports: [FormsModule, TranslateModule, AlertComponent, MobileUsePipe, EmailUsePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalInformationContactComponent implements OnInit, OnChanges {
  private userContext = inject(UserContextService);
  private translate = inject(TranslateService);

  @Input() member: Member;
  @Input() isUpdating: boolean;
  @Input() serverErrors: { email?: string; phone?: string; general?: string } | null;

  @Output() updatePersonalInformationEvent = new EventEmitter<MemberUpdate>();

  email: Email;
  mobilePhone: Phone | null = null;
  landlinePhone: Phone | null = null;

  updatedEmail = '';
  updatedMobilePhone = '';

  eContactPermission = ContactPermission;
  mailContactPermission: ContactPermission;
  mobileContactPermission: ContactPermission;

  isEditContactOpen = false;
  submitted = false;
  mobileTouched = false;
  emailTouched = false;

  serverEmailMsg = '';
  serverMobileMsg = '';
  formGeneralError = '';

  ngOnInit() {
    this.initializePermissions();
    this.initializePhones();
    this.initializeEmail();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.member && changes.member.currentValue) {
      this.member = changes.member.currentValue;
      this.onCloseEditContact();
    }

    if (changes.serverErrors) {
      this.serverEmailMsg = '';
      this.serverMobileMsg = '';
      this.formGeneralError = '';

      const errs = this.serverErrors ?? null;
      if (errs) {
        const mapped = mapContactUpdateErrorByCode(errs, (k, p) => this.translate.instant(k, p));
        this.serverMobileMsg = mapped.phone ?? (errs.phone ?? '');
        this.serverEmailMsg = mapped.email ?? (errs.email ?? '');
        this.formGeneralError = mapped.general ?? (errs.general ?? '');
        this.submitted = true;
        this.isEditContactOpen = true;
      }
    }
  }

  onOpenEditContact() { this.isEditContactOpen = true; }
  onCloseEditContact() { this.onReset(); this.isEditContactOpen = false; }

  onReset() {
    this.initializePhones();
    this.initializeEmail();
    this.initializePermissions();

    this.submitted = false;
    this.mobileTouched = false;
    this.emailTouched = false;

    this.serverEmailMsg = '';
    this.serverMobileMsg = '';
    this.formGeneralError = '';
  }

  isInternal() { return this.userContext.isInternalUser(); }

  emailPermission(): boolean { return this.isN(this.mailContactPermission); }
  mobilePermission(): boolean { return this.isN(this.mobileContactPermission); }

  get needMobile() { return !this.isN(this.mobileContactPermission); }
  get needEmail() { return !this.isN(this.mailContactPermission); }
  get hasMobile() { return !!this.updatedMobilePhone?.trim(); }
  get hasEmail() { return !!this.updatedEmail?.trim(); }
  get mobileError(): boolean {
    return !!this.serverMobileMsg ||
      (this.needMobile && !this.hasMobile && (this.mobileTouched || this.submitted));
  }
  get emailError(): boolean {
    return !!this.serverEmailMsg ||
      (this.needEmail && !this.hasEmail && (this.emailTouched || this.submitted));
  }
  get mobileErrorMessage(): string {
    return this.serverMobileMsg || this.translate.instant('USER_FEEDBACK.mobile_please');
  }
  get emailErrorMessage(): string {
    return this.serverEmailMsg || this.translate.instant('USER_FEEDBACK.email_please');
  }
  get canSubmit(): boolean {
    return (!this.needMobile || this.hasMobile) &&
      (!this.needEmail || this.hasEmail) &&
      !this.isUpdating;
  }

  onMobileChanged(val: string) {
    this.updatedMobilePhone = (val ?? '').replace(/[^0-9\s-+]/g, '').trim();
    this.mobileTouched = true;
    this.serverMobileMsg = '';
  }
  onEmailChanged(val: string) {
    this.updatedEmail = (val ?? '').trim();
    this.emailTouched = true;
    this.serverEmailMsg = '';
  }
  onMobilePermChanged(_: unknown) {
    this.mobileTouched = true;
    this.serverMobileMsg = '';
  }
  onMailPermChanged(_: unknown) {
    this.emailTouched = true;
    this.serverEmailMsg = '';
  }

  onSubmitUpdateContact() {
    this.submitted = true;

    if (this.mobileError || this.emailError) return;

    const phone = this.mobilePermission() ? '' : this.concatenateMobilePhone();
    const email = this.emailPermission() ? '' : (this.updatedEmail || '').trim();

    const updatedPersonalInformation = new PersonalInformationUpdate(phone, email);
    const updatedMember = new MemberUpdate(
      this.mailContactPermission,
      this.mobileContactPermission,
      updatedPersonalInformation
    );

    this.updatePersonalInformationEvent.emit(updatedMember);
  }

  onPhoneKeyDown(event: KeyboardEvent) {
    const allowed = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    const isCtrl = event.ctrlKey && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (allowed.includes(event.key) || isCtrl) return;
    const isDigit = /[0-9]/.test(event.key);
    const isSep = [' ', '-'].includes(event.key);
    if (!isDigit && !isSep) event.preventDefault();
  }

  private initializePermissions() {
    this.mobileContactPermission = this.member?.mobileContactPermission ?? ContactPermission.N;
    this.mailContactPermission = this.member?.mailContactPermission ?? ContactPermission.N;
  }

  private initializeEmail() {
    this.updatedEmail = '';
    this.email = this.member?.personalInformation?.emails?.[0] ?? null;
    if (this.email) this.updatedEmail = this.email.emailAddress.trim();
  }

  private initializePhones() {
    this.landlinePhone = this.getPhoneByType('FIX') ?? this.getPhoneByType('PHONE');
    this.mobilePhone = this.getPhoneByType('MOBILE');
    this.updatedMobilePhone = (this.mobilePhone?.number ?? '').trim();
  }

  private getPhoneByType(type: 'MOBILE' | 'FIX' | 'PHONE'): Phone | null {
    const p = this.member?.personalInformation?.phones
      ?.find((x: Phone) => x?.phoneType?.toUpperCase() === type) ?? null;
    return p ?? null;
  }

  private concatenateMobilePhone(): string {
    if (!this.updatedMobilePhone) return '';
    return this.updatedMobilePhone
      .trim()
      .replace(/\s|-/g, '')
      .replace(/^00/, '+');
  }

  private isN(val: ContactPermission): boolean {
    return val === ContactPermission.N;
  }

}
